import React, { Suspense, useEffect, useMemo } from 'react';
import { useDrop, XYCoord } from 'react-dnd';
import { useLazyLoadQuery } from 'react-relay/hooks';

import { datadogRum } from '@datadog/browser-rum';
import * as Fullstory from '@fullstory/browser';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { ErrorBoundary, ErrorBoundaryApp } from 'core';
import { get } from 'lodash';

import { EAppBarStaffVariant, EModule, FederatedModule, IAppBarStaffProps } from '@lumahealthhq/ui-common';

import useLocalStorage from '@lib/hooks/useLocalStorage';
import { ProvisioningEnum } from '@lib/resources/types';

import { FabHubJr } from 'deprecated/fragments';

import AppSideMenu from 'components/AppSideMenu';
import ZendeskWidget from 'components/AppSideMenu/components/ZendeskWidget';
import { BoxDnd, BoxDndDragItem } from 'components/v2';
import AccessDeniedPage from 'containers/AccessDeniedPage';
import Header from 'containers/Header';
import usePermissions from 'hooks/usePermissions';
import Breadcrumb from 'screens/MissionControl/components/Breadcrumb';
import SettingsDrawerMenu from 'screens/MissionControl/components/SettingsDrawerMenu';
import useMissionControlRedirect from 'screens/MissionControl/hooks/useMissionControlRedirectUser';

import { AppContentQuery } from './__generated__/AppContentQuery.graphql';
import { useBrandingContext, useDatadogStartSessionReplayRecording, useNextyContext } from './hooks';
import useRUMData from './hooks/useRUMData';

const NotificationsBell = React.lazy(() => import('containers/Header/components/Notifications'));

const styles = ({ zIndex, spacing: { unit } }: Theme) =>
  createStyles({
    fabHubJrWrapper: {
      zIndex: zIndex.snackbar + 1,
    },
    container: {
      width: '100%',
      display: 'flex',
      overflow: 'hidden',
    },
    containerV2: {
      flexDirection: 'column',
    },
    containerNextyNext: {
      marginTop: 64,
      overflow: 'auto',
    },
  });

export interface AppContentProps extends WithStyles<typeof styles>, React.HTMLAttributes<HTMLDivElement> {
  pathname: string;
  withZendesk?: boolean;
  withHeader?: boolean;
}

const AppContent: React.FC<AppContentProps> = ({ classes, children, pathname, withZendesk, withHeader, ...rest }) => {
  const { hasAccessToProduct, hasAccessToPathname } = usePermissions();
  const { toggleNextyNextStorage, nextyStorageValue, hideNextyNextBanner, shouldHideNextyBanner } = useNextyContext();
  const { isCobrandingEnabled, cobrandingLogo } = useBrandingContext();

  const isNewFormBuilder = /v2\/form-builder/.test(pathname);
  const isSettingsV2 = /v2\/settings/.test(pathname);
  const isBackofficeApp = pathname.startsWith('/backoffice');
  const isTelehealth = pathname.startsWith('/telehealth') || pathname.startsWith('/v2/telehealth');
  const route = isNewFormBuilder ? '/v2/settings/utilities/(:variant)-builder' : get(children, 'props.route.path');
  const includeFabHubJr = hasAccessToProduct(ProvisioningEnum.Chat);
  const userData = useRUMData();

  useDatadogStartSessionReplayRecording();

  useEffect(() => {
    if (userData?.id) {
      datadogRum.setUser(userData);

      Fullstory.identify(userData.id, {
        app: 'web-app',
        displayName: userData.name,
        ...userData,
      });
    }
  }, [userData, userData?.id]);

  // Access Control for Mission Control
  const hasNoAccess = useMissionControlRedirect(pathname);

  const { viewer } = useLazyLoadQuery<AppContentQuery>(
    graphql`
      query AppContentQuery($includeFabHubJr: Boolean = false, $skip: Boolean = false) {
        viewer {
          ...AppSideMenu_Viewer @skip(if: $skip)
          ...FabHubJr_Viewer @include(if: $includeFabHubJr) @skip(if: $skip)
        }
      }
    `,
    { includeFabHubJr, skip: nextyStorageValue },
  );

  const [fabHubJrDndItem, setFabHubJrDndItem] = useLocalStorage(
    'lh:AppContent:fabChatDndItem',
    {
      type: 'box',
      bottom: 56,
      right: 24,
    },
    true,
  );

  const [, drop] = useDrop({
    accept: fabHubJrDndItem.type,
    drop: (item: BoxDndDragItem, monitor) => {
      const delta = monitor.getDifferenceFromInitialOffset() as XYCoord;
      setFabHubJrDndItem((prevValue: BoxDndDragItem) => ({
        ...prevValue,
        bottom: Math.round(item.bottom - delta.y),
        right: Math.round(item.right - delta.x),
      }));
    },
  });

  const accessDenied = useMemo(() => {
    const hasReferralsAccess =
      /\/referrals?/.test(pathname) &&
      hasAccessToProduct([ProvisioningEnum.Referral, ProvisioningEnum.OutboundReferral]);
    const hasFormBuilderAccess =
      /\/settings\/features\/forms/.test(pathname) &&
      hasAccessToProduct([ProvisioningEnum.IntakeForm, ProvisioningEnum.PrequalForm, ProvisioningEnum.Feedback]);
    const hasSchedulerAccess =
      /\/settings\/features\/scheduler/.test(pathname) &&
      hasAccessToProduct([ProvisioningEnum.Scheduler, ProvisioningEnum.PrequalForm]);
    const hasReportsApptTabAccess =
      /\/appointments/.test(pathname) &&
      hasAccessToProduct([
        ProvisioningEnum.Reminder,
        ProvisioningEnum.Waitlist,
        ProvisioningEnum.Scheduler,
        ProvisioningEnum.IntakeForm,
        ProvisioningEnum.PrequalForm,
        ProvisioningEnum.Telehealth,
      ]);
    const hasOffersAccess =
      /\/offers/.test(pathname) && hasAccessToProduct([ProvisioningEnum.Waitlist, ProvisioningEnum.Scheduler]);
    const hasReminderTabAccess =
      /\/reminders/.test(pathname) &&
      hasAccessToProduct([
        ProvisioningEnum.Reminder,
        ProvisioningEnum.Feedback,
        ProvisioningEnum.Followup,
        ProvisioningEnum.Referral,
        ProvisioningEnum.OutboundReferral,
        ProvisioningEnum.Recall,
        ProvisioningEnum.IntakeForm,
        ProvisioningEnum.PrequalForm,
        ProvisioningEnum.Telehealth,
      ]);

    const hasInsightsCenterAccess = /\/insights-center/.test(pathname);

    const hasAccessToCopayDetermination =
      /copay-determination/i.test(pathname) && hasAccessToProduct(ProvisioningEnum.InsuranceVerification);

    // prevent access-denied redirect on patient chat log page
    return (
      !/\/(chat|journey)/.test(pathname) &&
      !hasAccessToPathname(pathname) &&
      !hasReferralsAccess &&
      !hasFormBuilderAccess &&
      !hasSchedulerAccess &&
      !hasReportsApptTabAccess &&
      !hasReminderTabAccess &&
      !hasOffersAccess &&
      !hasInsightsCenterAccess &&
      !hasAccessToCopayDetermination
    );
  }, [pathname, hasAccessToProduct, hasAccessToPathname]);

  if (!viewer || hasNoAccess) {
    return null;
  }

  return (
    <>
      {withZendesk && (
        <ZendeskWidget
          isCobrandingEnabled={isCobrandingEnabled}
          nextyStorageValue={nextyStorageValue}
          cobrandingLogo={cobrandingLogo}
        />
      )}

      {withHeader &&
        (nextyStorageValue ? (
          <FederatedModule<IAppBarStaffProps>
            module={EModule.AppBarStaff}
            fallback={null}
            props={{
              sx: (theme) => ({ borderBottom: `1px solid ${theme.palette.divider}` }),
              currentPathname: pathname,
              toggleNexty: toggleNextyNextStorage,
              NotificationsBell,
              hideNextyBanner: hideNextyNextBanner,
              shouldHideNextyBanner: !!shouldHideNextyBanner,
              variant: isBackofficeApp ? EAppBarStaffVariant.Backoffice : EAppBarStaffVariant.Default,
            }}
          />
        ) : (
          <Header
            variant={isSettingsV2 || isNewFormBuilder ? 'dark' : 'default'}
            menuButtonVariant={isSettingsV2 || isNewFormBuilder ? 'avatar' : 'default'}
          />
        ))}
      <div ref={drop} {...rest}>
        {!nextyStorageValue &&
          pathname.indexOf('/dev/') !== 0 &&
          !isSettingsV2 &&
          !isNewFormBuilder &&
          !isTelehealth &&
          !isBackofficeApp && <AppSideMenu fragmentRefViewer={viewer} pathname={pathname} />}
        {(isSettingsV2 || isNewFormBuilder) && !isTelehealth && <SettingsDrawerMenu />}
        {/* Settings V2 manages access within its scope */}
        {!(isSettingsV2 || isNewFormBuilder) && !isTelehealth && accessDenied ? (
          <AccessDeniedPage />
        ) : (
          <ErrorBoundaryApp>
            <div
              className={classnames({
                [classes.container]: true,
                [classes.containerV2]: isSettingsV2 || isNewFormBuilder,
                [classes.containerNextyNext]: nextyStorageValue,
              })}
            >
              {(isSettingsV2 || isNewFormBuilder) && !isTelehealth && (
                <Breadcrumb
                  pathname={isNewFormBuilder ? '/v2/settings/utilities/form-builder' : pathname}
                  route={route}
                />
              )}

              {children}
            </div>
          </ErrorBoundaryApp>
        )}
        {!nextyStorageValue && includeFabHubJr && !/^\/hub|^\/patients\/.*\/chat/i.test(pathname) && (
          <BoxDnd className={classnames(classes.fabHubJrWrapper, 'no-print')} item={fabHubJrDndItem} hideSourceOnDrag>
            <FabHubJr fragmentRef={viewer} />
          </BoxDnd>
        )}
      </div>
    </>
  );
};

export default withStyles(styles)((props: AppContentProps) => (
  <ErrorBoundary>
    <Suspense fallback={null}>
      <AppContent {...props} />
    </Suspense>
  </ErrorBoundary>
));
